import { useQueryClient } from '@tanstack/react-query';
import { useSSESubscription } from '@/services/sse';
import { useTenantId } from './useTenant';

/**
 * Turns server pushes into cache invalidation.
 *
 * This is app policy, not transport — services/sse.js knows how to receive
 * an event, and this decides what it means. Kept apart so the mapping can be
 * read and changed without touching reconnect logic.
 *
 * The monolith re-ran every render function on every push. Here a reason
 * invalidates only the caches it can actually affect, so a Telegram message
 * arriving does not refetch the analytics scan.
 *
 * `driver_moved` is deliberately absent from this table: it fires every ~15s
 * per driver and carries the new position in its payload, so LiveMap consumes
 * it directly and moves one marker. Routing it through here would refetch the
 * driver list eighty times a minute for a fleet of twenty, to redraw data
 * that already arrived in the event.
 */
const REASON_PREFIXES = {
  // 'drivers' rides along on both of these: driver.status (idle/assigned/
  // loading/en_route/unloading) is derived server-side from the order it's
  // attached to (core/driver_status.py), not stored on the driver row —
  // reassigning a driver (order_edited) or advancing an order's lifecycle
  // (status_changed, including the transition to 'delivered' that frees a
  // driver back to idle) both change that derived value even though neither
  // touches the drivers table directly. Command Center's idle-driver roster
  // (components/command-center/DispatchMatrix.jsx) is what surfaced this —
  // without it, a driver stayed shown as busy for up to a minute after
  // their order was reassigned or delivered.
  order: ['orders', 'dashboard', 'drivers', 'worklist'],
  status_changed: ['orders', 'dashboard', 'drivers', 'worklist'],
  waybill_manual_entry: ['orders', 'dashboard'],
  ticket_ocr: ['orders', 'dashboard'],
  dwell: ['dwell-status', 'orders'],
  driver_created: ['drivers'],
  driver_updated: ['drivers', 'orders'],
  driver_deleted: ['drivers', 'orders'],
  driver_key_rotated: ['drivers'],
  // V-275. The counterpart next door (reset_driver_password()'s own
  // docstring's words) to driver_key_rotated above, and missing from this
  // table while that one is here: it can assign a driver's FIRST username
  // (a row predating the login backfill), which is rendered straight into
  // the driver list row. Without this, the second dispatcher's list keeps
  // showing the old (or no) username until something unrelated refetches it.
  driver_password_reset: ['drivers'],
  // V-276. blueprints/imports.py publishes `f"{entity_type}_imported"` for
  // every importable entity; this one had no entry at all (unlike
  // client_site below, which had the WRONG one), so it fell through to the
  // default. useImport.js's own AFFECTED_KEYS.driver — the reference for
  // what a driver import actually changes — is the same two caches.
  driver_imported: ['drivers', 'dashboard'],
  vehicle_defect: ['vehicle-defects', 'vehicles'],
  vehicle_compliance: ['vehicles', 'notifications'],
  // 'drivers' so the expiry badge on the row refreshes with the bell, rather
  // than staying green until something else happens to refetch the list.
  driver_compliance: ['drivers', 'notifications'],
  vehicle: ['vehicles'],
  // V-284. blueprints/vehicles.py's assign_vehicle_driver() moves
  // vehicles.current_driver_id, which is exactly what blueprints/
  // drivers.py's list_drivers() reads (via assigned_plate_by_driver(),
  // V-225/P0-5) to resolve each row's license_plate. Without its own,
  // more specific entry here, this reason fell through to the broader
  // `vehicle` rule above and never reached 'drivers' — so a SECOND
  // dispatcher's Drivers panel kept showing both the outgoing and the
  // incoming driver on their pre-reassignment trucks.
  vehicle_driver_assigned: ['vehicles', 'drivers'],
  // A driver picking a route writes cost onto the order, which is what the
  // Analytics margin is computed from — so this touches more than the order
  // list. `vignette_required` additionally raises a bell notification and
  // implies somebody is about to change a vehicle's vignette date.
  route_selected: ['orders', 'dashboard', 'analytics'],
  vignette_required: ['notifications', 'vehicles', 'orders'],
  // V-285. Confirmed the suspicion V-276's own origin notes recorded and
  // deliberately left unconfirmed ("quarry_price_imported negauna
  // quarries/analytics ... agentas jų NEPATVIRTINO"): blueprints/
  // reporting.py's _analytics() reads quarry_prices.cost_per_ton straight
  // off core.invoicing.PRICE_SUBSELECT into material_cost_per_ton, which
  // every margin figure on the tab is built from — the same dependency
  // useDataAdmin.js's useSaveQuarryPrice()/useDeleteQuarryPrice() already
  // invalidate 'analytics' for locally (V-37/V-277). This one entry covers
  // quarry_price_created/_updated/_deleted alike; without 'analytics' here,
  // a second console's Analytics tab kept the margin computed with the
  // price a colleague had just corrected or removed.
  quarry_price: ['quarry-prices', 'analytics'],
  // 'orders' rides along for the same reason driver_updated does above: the
  // orders table renders quarry and site names straight off its own rows, and
  // now the archived badge beside them too. Without this, archiving a quarry
  // leaves every open orders table showing it as live until something else
  // happens to refetch. Both are edited a handful of times a year, so the
  // extra refetch costs nothing worth counting.
  quarry: ['quarries', 'orders'],
  site: ['sites', 'orders'],
  material: ['materials'],
  telegram_message: ['telegram-messages'],
  telegram_template: ['telegram-templates'],
  telegram_driver_linked: ['drivers', 'telegram-messages'],
  telegram_button: ['orders', 'telegram-messages'],
  // V-279. 'analytics' rides along: fuel_cost_per_km/toll_cost_per_km/
  // driver_cost_per_hour_eur live on this same row and are read straight
  // into reporting.py's _analytics() margins. Without it, a second console
  // with Analytics open keeps showing margins computed with the rate a
  // colleague just corrected in Duomenys/Nustatymai.
  tenant: ['tenants', 'analytics'],
  // V-61. Longer than `tenant`, so it wins the longest-prefix match and
  // `tenant_user_created` / `tenant_user_updated` stop landing on the
  // settings cache. An owner switching a departed dispatcher's login off
  // has to reach the second console open on the same company — that list
  // is the only place either of them can see who still has a way in.
  tenant_user: ['tenant-users'],
  // A client request is not an order until it is approved, so a
  // submission invalidates only the approval queue. Approval is the
  // one that also touches orders/dashboard — that is the moment real
  // loads appear — and it creates the delivery site too when the
  // client had dropped a new pin.
  client_request_submitted: ['order-requests'],
  client_request_cancelled: ['order-requests'],
  client_request_rejected: ['order-requests'],
  client_request_approved: ['order-requests', 'orders', 'dashboard', 'sites'],
  client: ['clients'],
  // V-276. Longer than `client`, so it wins the longest-prefix match (same
  // mechanism as `tenant_user` over `tenant` above) — without this,
  // "client_site_imported" (blueprints/imports.py's bulk site import) matched
  // `client` instead, landing on `clients` alone. useImport.js's own
  // AFFECTED_KEYS.client_site — the reference for what a site import
  // actually changes — is `['sites', 'clients']`; this carries the same
  // answer to the second dispatcher's console, whose Sites panel otherwise
  // stayed exactly as it was after a colleague's import.
  client_site: ['sites', 'clients'],
  // Covers invoice_issued, invoice_payment, invoice_sent and
  // invoice_cancelled with one entry — all four move the same set. `orders` rides along because
  // issuing stamps orders.invoice_id / invoice_status and cancelling
  // unstamps them, and `invoice-draft` because a draft on another
  // dispatcher's screen is a list of loads that just changed hands.
  // 'unbilled' rides along: issuing an invoice is precisely what takes a
  // load off the unbilled sweep, so a stale panel would keep showing
  // money the dispatcher just billed.
  // 'worklist' rides along with every reason that moves money: the queue is
  // built from the unbilled sweep and the receivables book, so a stale one
  // sends a dispatcher to chase a debt a colleague has just marked paid —
  // or to re-send an invoice somebody else has just put in the post.
  invoice: ['invoices', 'receivables', 'invoice-draft', 'unbilled', 'orders',
            'dashboard', 'worklist'],
  // V-282. "Retry PODs" (POST .../pod-retry/run) publishes this once, for
  // the whole tenant, only when it actually generated at least one note —
  // useRetryPods()'s own onSuccess invalidates exactly this same set for
  // the browser that clicked it, with the same reasoning: a generated note
  // moves a load out of the no_pod bucket and into the invoice draft, the
  // worklist and month close, all of which were showing it as stuck. Without
  // this entry a second dispatcher's Billing panel kept showing that money
  // as stuck after a colleague fixed the disk/font issue and regenerated it.
  pod_generated: ['unbilled', 'invoice-draft', 'worklist', 'orders'],
  // V-11's two tables. Nothing stores a due date — core/servicing.py computes
  // every one of them at read time from these two tables — so a plan or a
  // record that changes has to invalidate the DERIVED surfaces too, not just
  // the list it belongs to. The writer's own hooks already do that for the
  // browser that made the change; this entry is what carries it to the second
  // dispatcher on the same console, whose Transport tab would otherwise keep
  // showing "NĖRA ISTORIJOS" against a truck a colleague has just recorded a
  // service for.
  //
  // All six reasons fell through to the default ['orders', 'dashboard'] until
  // this line existed: no prefix here matched them, so every write refetched
  // two caches it could not affect and none of the three it could.
  service_plan: ['service-plans', 'service-due'],
  // V-60 adds 'service-records': the history is a screen now, so a second
  // dispatcher correcting an odometer has to reach the first one's table.
  // Not on the `service_plan` line beside it — a rule changing moves every
  // deadline and no record.
  service_record: ['service-plans', 'service-due', 'service-on-time', 'service-records'],
  // The daily sweep, not a write. It touches no table the panel reads — it is
  // the bell that changes — but `service-due` rides along because a run that
  // has just decided a truck is overdue is the moment the panel beside the
  // bell is most likely to be wrong.
  service_digest: ['notifications', 'service-due'],
  // Fuel reaches the maintenance panel, which is not obvious: a fill carries
  // an odometer reading, and core/servicing.py resolves a truck's current
  // odometer from exactly those fills. A fill entered without this is a
  // kilometre deadline that silently stays yesterday's on every other screen.
  //
  // V-294. 'fuel-fills' rides along too: useCreateFuel()/useUpdateFuel()/
  // useDeleteFuel()/useLinkFuelCard() (useAnalytics.js) all invalidate it
  // locally already — FuelPanel always renders "+ Add fill" beside
  // "Patikrinti" (FuelFillsModal, useFuelFills()) for the same truck. Without
  // it here, a second dispatcher with that modal open kept seeing the fills
  // list a colleague had just added to, edited, deleted from, or linked a
  // card onto stay exactly as it was.
  fuel: ['fuel-report', 'fuel-fills', 'vehicles', 'service-due'],
  // Both money digests. They write no table either — but the bell is fed from
  // the same rows, and the panel each one is about is the first place a
  // dispatcher goes when it rings.
  unbilled_digest: ['notifications', 'unbilled'],
  receivables_reminder: ['notifications', 'receivables'],
  // V-372. A finding APPEARING was published from the start — the compliance
  // engine, the vignette check and vehicle_defect_reported all have rules
  // above. A finding being RESOLVED was published by nobody, so every
  // dispatcher except the one who pressed the button kept seeing it.
  //
  // Six caches, and the last three are the ones neither side was
  // invalidating: core/invoicing.py's _held_entity_ids() counts UNRESOLVED
  // findings as the billing hold, and V-335 records that pressing resolve is
  // the documented remedy for a load that cannot be invoiced. Same list, and
  // the same reason, as pod_generated above — a load stops being stuck and
  // three money screens are still showing it as stuck.
  system_event: ['notifications', 'fleet-integrity', 'client-reliability',
                 'unbilled', 'invoice-draft', 'worklist'],
  vacation_requested: ['leaves'],
  vacation_status_changed: ['leaves'],
};

function cachesFor(reason) {
  if (!reason) return ['orders', 'dashboard'];
  // Longest matching prefix wins, so `vehicle_defect_reported` lands on the
  // defect caches rather than being swallowed by the broader `vehicle` rule.
  const match = Object.keys(REASON_PREFIXES)
    .filter((prefix) => reason.startsWith(prefix))
    .sort((a, b) => b.length - a.length)[0];
  return match ? REASON_PREFIXES[match] : ['orders', 'dashboard'];
}

export function useLiveSync() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  useSSESubscription('*', (payload) => {
    if (payload.reason === 'driver_moved') return; // handled by LiveMap
    cachesFor(payload.reason).forEach((prefix) => {
      // `tenants` is the one cache keyed without a tenant id, since it is
      // what resolves that id in the first place.
      queryClient.invalidateQueries({
        queryKey: prefix === 'tenants' || !tenantId ? [prefix] : [prefix, tenantId],
      });
    });
  });
}
